import type { NonEmptyWorkingTimes, WeekDays, WeekDaySpec, WorkingTime } from '@bigint-project/core';
import { hhmmToMinutes, minutesToHHmm, workingTime } from './time-format.js';
import { WEEKDAY_PRESETS, buildWeekDayPreset, type WeekDayPresetId } from './weekday-presets.js';

export interface WeekDayEditorProps {
  readonly weekDays: WeekDays;
  /** 有 base calendar 時，該 base 解析出的每日設定（供「未指定 → 繼承」列顯示繼承到的內容）。 */
  readonly baseWeekDays?: WeekDays | null;
  readonly onChange: (next: WeekDays) => void;
}

const DAY_LABELS = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'] as const;

type TriState = 'working' | 'non-working' | 'inherit';

function specState(spec: WeekDaySpec | undefined): TriState {
  if (spec === null || spec === undefined) return 'inherit';
  return spec.working ? 'working' : 'non-working';
}

/** working=true 時取出 times；其餘（包含 undefined／null／working=false）回傳 null。 */
function timesOf(spec: WeekDaySpec | undefined): NonEmptyWorkingTimes | null {
  if (spec === null || spec === undefined || !spec.working) return null;
  return spec.times;
}

function formatRange(t: WorkingTime): string {
  return `${minutesToHHmm(t.start as number)}–${minutesToHHmm(t.end as number)}`;
}

function replaceDay(weekDays: WeekDays, index: number, spec: WeekDaySpec): WeekDays {
  const next = weekDays.slice() as WeekDaySpec[];
  next[index] = spec;
  return next as unknown as WeekDays;
}

function asNonEmpty(segments: readonly WorkingTime[]): NonEmptyWorkingTimes {
  if (segments.length === 0) return [workingTime(9 * 60, 18 * 60)];
  return segments as unknown as NonEmptyWorkingTimes;
}

/**
 * 每週工作時間編輯器：三態(工作／非工作／未指定繼承) + 多段時段編輯 + 一鍵套用預設。
 * 純展示元件：props in / callbacks out。零 store、零 Tauri 相依。
 */
export function WeekDayEditor({ weekDays, baseWeekDays, onChange }: WeekDayEditorProps): React.JSX.Element {
  const setState = (index: number, state: TriState): void => {
    if (state === 'inherit') {
      onChange(replaceDay(weekDays, index, null));
      return;
    }
    if (state === 'non-working') {
      onChange(replaceDay(weekDays, index, { working: false }));
      return;
    }
    const currentTimes = timesOf(weekDays[index]);
    onChange(replaceDay(weekDays, index, { working: true, times: currentTimes ?? asNonEmpty([]) }));
  };

  const setSegment = (index: number, segIndex: number, field: 'start' | 'end', text: string): void => {
    const times = timesOf(weekDays[index]);
    if (times === null) return;
    const value = hhmmToMinutes(text);
    if (value === null) return;
    const segments = times.map((seg, i) => {
      if (i !== segIndex) return seg;
      const start = field === 'start' ? value : (seg.start as number);
      const end = field === 'end' ? value : (seg.end as number);
      return workingTime(start, end);
    });
    onChange(replaceDay(weekDays, index, { working: true, times: asNonEmpty(segments) }));
  };

  const addSegment = (index: number): void => {
    const times = timesOf(weekDays[index]);
    if (times === null) return;
    const last = times[times.length - 1];
    const lastEnd = last !== undefined ? (last.end as number) : 0;
    const start = Math.min(lastEnd, 1440 - 60);
    const segments = [...times, workingTime(start, Math.min(start + 60, 1440))];
    onChange(replaceDay(weekDays, index, { working: true, times: asNonEmpty(segments) }));
  };

  const removeSegment = (index: number, segIndex: number): void => {
    const times = timesOf(weekDays[index]);
    if (times === null || times.length <= 1) return;
    const segments = times.filter((_, i) => i !== segIndex);
    onChange(replaceDay(weekDays, index, { working: true, times: asNonEmpty(segments) }));
  };

  const applyPreset = (preset: WeekDayPresetId): void => {
    onChange(buildWeekDayPreset(preset));
  };

  return (
    <div data-testid="weekday-editor" className="text-sm">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-fg-muted">套用預設：</span>
        {WEEKDAY_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            title={preset.description}
            data-testid={`weekday-preset-${preset.id}`}
            className="rounded border border-line-strong px-2 py-1 text-xs text-fg hover:bg-raised"
            onClick={() => applyPreset(preset.id)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <table className="w-full border-collapse">
        <thead>
          <tr className="text-left text-xs text-fg-muted">
            <th className="w-16 pb-1">星期</th>
            <th className="w-40 pb-1">狀態</th>
            <th className="pb-1">工作時段</th>
          </tr>
        </thead>
        <tbody>
          {DAY_LABELS.map((label, index) => {
            const spec = weekDays[index];
            const state = specState(spec);
            const times = timesOf(spec);
            const baseSpec = baseWeekDays !== null && baseWeekDays !== undefined ? baseWeekDays[index] : undefined;
            const baseTimes = timesOf(baseSpec);
            return (
              <tr
                key={label}
                data-testid={`weekday-row-${index}`}
                className="border-t border-hairline"
              >
                <td className="py-1.5 font-medium text-fg">{label}</td>
                <td className="py-1.5">
                  <select
                    aria-label={`${label} 狀態`}
                    data-testid={`weekday-state-${index}`}
                    className="rounded border border-line-strong bg-surface px-1.5 py-0.5 text-xs text-fg dark:bg-raised"
                    value={state}
                    onChange={(e) => setState(index, e.target.value as TriState)}
                  >
                    <option value="working">工作</option>
                    <option value="non-working">非工作</option>
                    <option value="inherit">未指定（繼承）</option>
                  </select>
                </td>
                <td className="py-1.5">
                  {state === 'working' && times !== null ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {times.map((segment, segIndex) => (
                        <span
                          key={segIndex}
                          data-testid={`weekday-segment-${index}-${segIndex}`}
                          className="flex items-center gap-1 rounded border border-line bg-raised px-1.5 py-0.5"
                        >
                          <input
                            aria-label={`${label} 第 ${segIndex + 1} 段開始`}
                            className="w-14 bg-transparent text-xs text-fg outline-none"
                            defaultValue={minutesToHHmm(segment.start as number)}
                            onBlur={(e) => setSegment(index, segIndex, 'start', e.target.value)}
                          />
                          <span className="text-fg-subtle">–</span>
                          <input
                            aria-label={`${label} 第 ${segIndex + 1} 段結束`}
                            className="w-14 bg-transparent text-xs text-fg outline-none"
                            defaultValue={minutesToHHmm(segment.end as number)}
                            onBlur={(e) => setSegment(index, segIndex, 'end', e.target.value)}
                          />
                          {times.length > 1 ? (
                            <button
                              type="button"
                              aria-label={`移除 ${label} 第 ${segIndex + 1} 段`}
                              className="text-fg-subtle hover:text-danger"
                              onClick={() => removeSegment(index, segIndex)}
                            >
                              ×
                            </button>
                          ) : null}
                        </span>
                      ))}
                      <button
                        type="button"
                        aria-label={`新增 ${label} 時段`}
                        className="text-xs text-blue-600 hover:underline dark:text-focus"
                        onClick={() => addSegment(index)}
                      >
                        + 時段
                      </button>
                    </div>
                  ) : state === 'non-working' ? (
                    <span data-testid={`weekday-non-working-${index}`} className="text-xs text-fg-subtle">
                      不工作
                    </span>
                  ) : (
                    <span
                      data-testid={`weekday-inherit-${index}`}
                      className="text-xs italic text-amber-600 dark:text-warn"
                    >
                      繼承自 base
                      {baseSpec !== null && baseSpec !== undefined
                        ? baseTimes !== null
                          ? `：工作 ${baseTimes.map(formatRange).join('、')}`
                          : '：不工作'
                        : ''}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
